// Port Wingo UI (React 19 + Tailwind 4) → Tailwind 3.4, preluat din portul ERP-ului (React 18). Vezi components/wingo/README.md.
import { useCallback, useEffect, useRef, useState } from "react";

// Wingo UI Pro · https://wingo-ui.com/legal/license

// copies text (and optionally html) to the clipboard. navigator.clipboard where it exists (https, localhost),
// a hidden textarea + execCommand elsewhere (plain http on a LAN ip, old webviews). resolves true on success
export async function copyToClipboard(text: string, options: { html?: string } = {}): Promise<boolean> {
  if (typeof window === "undefined") return false;
  try {
    if (options.html && typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
      await navigator.clipboard.write([
        new ClipboardItem({
          "text/plain": new Blob([text], { type: "text/plain" }),
          "text/html": new Blob([options.html], { type: "text/html" }),
        }),
      ]);
      return true;
    }
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // permission denied or not focused: try the legacy path below
  }
  return legacyCopy(text);
}

function legacyCopy(text: string): boolean {
  const active = document.activeElement as HTMLElement | null;
  const area = document.createElement("textarea");
  area.value = text;
  area.setAttribute("readonly", "");
  // off screen, and 16px so ios does not zoom when it selects
  area.style.cssText = "position:fixed;top:0;left:-9999px;opacity:0;font-size:16px";
  document.body.appendChild(area);
  area.select();
  area.setSelectionRange(0, text.length);
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } catch {
    ok = false;
  }
  area.remove();
  active?.focus?.({ preventScroll: true });
  return ok;
}

export type CopySource = string | (() => string | Promise<string>);

export type UseClipboardOptions = {
  // ms the copied state stays true (the check mark); 0 keeps it until reset() or the next copy
  timeout?: number;
  onCopy?: (text: string) => void;
  onError?: (error: Error) => void;
};

export type UseClipboardResult = {
  // copies a string, or the result of a function (read the latest value at click time); resolves true on success
  copy: (source: CopySource, options?: { html?: string }) => Promise<boolean>;
  // true for `timeout` ms after a successful copy
  copied: boolean;
  // the last failure, cleared by the next copy
  error: Error | null;
  // the last text copied
  text: string | null;
  reset: () => void;
};

// a copy action with a "copied" flag that resets itself: copy buttons, copy-on-click values, share fallbacks
export function useClipboard({ timeout = 2000, onCopy, onError }: UseClipboardOptions = {}): UseClipboardResult {
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [text, setText] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const callbacks = useRef({ onCopy, onError });
  useEffect(() => {
    callbacks.current = { onCopy, onError };
  });

  useEffect(() => () => clearTimeout(timer.current), []);

  const reset = useCallback(() => {
    clearTimeout(timer.current);
    setCopied(false);
    setError(null);
  }, []);

  const copy = useCallback(
    async (source: CopySource, options?: { html?: string }) => {
      clearTimeout(timer.current);
      setError(null);
      let value: string;
      try {
        value = typeof source === "function" ? await source() : source;
      } catch (reason) {
        const failure = reason instanceof Error ? reason : new Error(String(reason));
        setCopied(false);
        setError(failure);
        callbacks.current.onError?.(failure);
        return false;
      }
      const ok = await copyToClipboard(value, options);
      if (!ok) {
        const failure = new Error("Copy failed");
        setCopied(false);
        setError(failure);
        callbacks.current.onError?.(failure);
        return false;
      }
      setText(value);
      setCopied(true);
      callbacks.current.onCopy?.(value);
      if (timeout > 0) timer.current = setTimeout(() => setCopied(false), timeout);
      return true;
    },
    [timeout],
  );

  return { copy, copied, error, text, reset };
}
